'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { getImageDimensions } from '@sanity/asset-utils';
import { sanityImageUrl } from '@/lib/utils';
import { useCarouselApi } from '@/hooks/useCarouselApi';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

type CarouselSlide = {
  _key: string;
  image: { asset: { _ref: string } };
  caption?: string;
};

export default function RichTextCarousel({
  slides,
}: {
  slides: CarouselSlide[];
}) {
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } =
    useCarouselApi();

  if (!slides?.length) return null;

  const progress = count > 1 ? (current - 1) / (count - 1) : 1;

  return (
    <div className="flex flex-col gap-[1.5rem] w-full">
      <Carousel
        setApi={setApi}
        opts={{ align: 'start', loop: false }}
        className="w-full"
      >
        <CarouselContent className="gap-[1.5rem]">
          {slides.map((slide) => {
            if (!slide.image?.asset?._ref) return null;

            const url = sanityImageUrl(slide.image.asset._ref);
            const { width, height } = getImageDimensions(
              slide.image.asset._ref,
            );

            return (
              <CarouselItem className="basis-auto" key={slide._key}>
                <div className="w-[40rem] h-auto rounded-sm overflow-clip">
                  <Image
                    src={{ src: url, width, height }}
                    alt={slide.caption || ''}
                    quality={90}
                  />
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {count > 1 && (
        <div className="flex items-center gap-[2rem] w-[40rem] mx-auto">
          <button
            type="button"
            onClick={handlePrev}
            disabled={isFirst}
            aria-label="Previous"
            className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black/10 transition-opacity disabled:opacity-30 hover:opacity-70"
          >
            <ArrowIcon className="h-[2.125rem] w-[2.125rem] rotate-180 text-black" />
          </button>

          <div className="h-[0.5rem] flex-1 overflow-hidden rounded-[3rem] bg-black/10">
            <motion.div
              className="h-full rounded-full bg-black"
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.3, ease }}
            />
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={isLast}
            aria-label="Next"
            className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity disabled:opacity-30 hover:opacity-80"
          >
            <ArrowIcon className="h-[2.125rem] w-[2.125rem] text-white" />
          </button>
        </div>
      )}
    </div>
  );
}
